import React, { useState } from 'react';
import { HardHat, Hammer, ClipboardList, ShieldCheck, DollarSign, Users, Search, Wrench, Clock, TrendingDown, ArrowRight, CheckCircle, AlertTriangle, BarChart3, FileText, Building2, ChevronDown, ChevronUp } from 'lucide-react';

const PAIN_POINTS = [
  {
    id: 'davis-bacon',
    icon: FileText,
    color: 'purple',
    title: 'Davis-Bacon Automation',
    tag: 'Every project. Every contract.',
    problem: 'COs spend 30–90 minutes manually looking up DOL wage determinations for every construction contract. Wrong county or stale determination = protest.',
    solution: 'AcuSightPro Construction auto-detects your project location and construction type, pulls the correct wage determination from the DOL database, embeds it in the solicitation, and alerts you before it expires.',
    timeSaved: '30–90 min saved per project',
    existing: 'Extend',
  },
  {
    id: 'ige',
    icon: DollarSign,
    color: 'blue',
    title: 'Construction IGE Builder',
    tag: 'GAO flagged this as broken.',
    problem: 'Construction cost estimates require RSMeans unit costs, material quantities, crew compositions, and Davis-Bacon labor rates. Most federal construction COs use spreadsheets. GAO has called out weak IGE quality as a government-wide systemic problem.',
    solution: 'Describe your scope in plain English. ASCP\'s AI decomposes it into a full Work Breakdown Structure, maps each element to cost benchmarks from federal construction award history, and produces a range-based IGE with full audit trail.',
    timeSaved: '8–40 hrs → 1–4 hrs per IGE',
    existing: 'New',
  },
  {
    id: 'nrm-pipeline',
    icon: BarChart3,
    color: 'indigo',
    title: 'Maintenance Pipeline',
    tag: '30–80 projects. One CO. One dashboard.',
    problem: 'Federal facilities run dozens of concurrent maintenance projects: roofs, HVAC, plumbing, electrical. All tracked in spreadsheets. Obligation deadlines slip. Budgets drift. Nothing is visible until it is a problem.',
    solution: 'Multi-project Kanban with budget vs. actuals, obligation deadline tracking, AI-powered risk scoring, and one-click monthly maintenance status reports. Every project visible. Every deadline known.',
    timeSaved: '4–8 hrs/month on status reports alone',
    existing: 'New',
  },
  {
    id: 'market-research',
    icon: Search,
    color: 'teal',
    title: 'SDVOSB Market Research',
    tag: 'Federal Rule of Two. Construction edition.',
    problem: 'Finding two capable, certified small business general contractors within a reasonable distance for every set-aside construction project is time-consuming and inconsistent. Thin markets in rural areas get set-aside determinations wrong.',
    solution: 'Construction-specific small business search by NAICS (236/237/238), geographic radius, bonding capacity, and federal past performance. Auto-generates the Rule of Two determination memo ready for the file.',
    timeSaved: '4–16 hrs → 30 min per action',
    existing: 'Extend',
  },
  {
    id: 'solicitation',
    icon: ClipboardList,
    color: 'violet',
    title: 'Construction Solicitation Studio',
    tag: 'IFB. Design-Build. FAR Part 36.',
    problem: 'Construction solicitations are not RFPs. IFBs use sealed bidding. Design-Build has a two-phase structure. FAR Part 36 and agency supplemental regs have construction-specific clauses that services COs miss. One missed clause can trigger a protest.',
    solution: 'IFB, Design-Build, and RFP templates. FAR Part 36 clauses auto-populated based on project type, dollar value, and complexity. Miller Act bond thresholds enforced. Liquidated damages calculated and claused.',
    timeSaved: '10–30 hrs → 2–5 hrs per solicitation',
    existing: 'Extend',
  },
  {
    id: 'joc',
    icon: Wrench,
    color: 'amber',
    title: 'JOC Task Order Manager',
    tag: 'IDIQ ceiling. Coefficients. Pre-priced vs. NPP.',
    problem: 'Job Order Contracting is a dominant vehicle for federal facility maintenance. Managing task order pricing (coefficient × unit price), tracking IDIQ ceiling utilization, and flagging non-pre-priced items is a manual, error-prone process.',
    solution: 'Load your JOC unit price book. Enter scope. ASCP calculates task order pricing, tracks ceiling utilization, flags NPP items before they breach the 10% threshold, and generates the complete task order package.',
    timeSaved: '2–8 hrs → 30 min per task order',
    existing: 'New',
  },
  {
    id: 'ae-selection',
    icon: Building2,
    color: 'cyan',
    title: 'A/E Selection Workflow',
    tag: 'Brooks Act. SF 330. 6% fee cap.',
    problem: 'Architect-Engineer services are qualifications-based under the Brooks Act, with no price factor in the selection. Many COs handle A/E infrequently and fumble SF 330 evaluations, fee negotiations, and Selection Board Reports.',
    solution: 'SF 330 PDF ingestion and structured extraction. Qualifications-based scoring matrix. Short-list workflow. Fee negotiation memo with FAR 36.606 6% fee cap calculated automatically. Selection Board Report generated from scoring data.',
    timeSaved: '8–20 hrs → 2–4 hrs per selection',
    existing: 'Extend',
  },
  {
    id: 'safety',
    icon: ShieldCheck,
    color: 'green',
    title: 'Safety Intelligence',
    tag: 'OSHA 1926. Agency Standards. ICRA.',
    problem: 'Contractor-submitted Accident Prevention Plans (APPs) are supposed to be reviewed before breaking ground. Most COs rubber-stamp them. In an active occupied federal facility, a missed safety gap creates serious liability.',
    solution: 'Upload the APP. ASCP checks it against OSHA 29 CFR Part 1926, agency technical standards, and Infection Control Risk Assessment (ICRA) requirements for active occupied facilities. Deficiency letter drafted automatically.',
    timeSaved: '2–6 hrs → 30 min per safety review',
    existing: 'New (Phase 2)',
  },
];

const COLOR_MAP = {
  purple: { badge: 'bg-purple-100 text-purple-800 border-purple-200', icon: 'bg-purple-100 text-purple-600', ring: 'ring-purple-500/20', border: 'border-purple-200', tag: 'bg-purple-50 text-purple-700' },
  blue: { badge: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'bg-blue-100 text-blue-600', ring: 'ring-blue-500/20', border: 'border-blue-200', tag: 'bg-blue-50 text-blue-700' },
  indigo: { badge: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: 'bg-indigo-100 text-indigo-600', ring: 'ring-indigo-500/20', border: 'border-indigo-200', tag: 'bg-indigo-50 text-indigo-700' },
  teal: { badge: 'bg-teal-100 text-teal-800 border-teal-200', icon: 'bg-teal-100 text-teal-600', ring: 'ring-teal-500/20', border: 'border-teal-200', tag: 'bg-teal-50 text-teal-700' },
  violet: { badge: 'bg-violet-100 text-violet-800 border-violet-200', icon: 'bg-violet-100 text-violet-600', ring: 'ring-violet-500/20', border: 'border-violet-200', tag: 'bg-violet-50 text-violet-700' },
  amber: { badge: 'bg-amber-100 text-amber-800 border-amber-200', icon: 'bg-amber-100 text-amber-600', ring: 'ring-amber-500/20', border: 'border-amber-200', tag: 'bg-amber-50 text-amber-700' },
  cyan: { badge: 'bg-cyan-100 text-cyan-800 border-cyan-200', icon: 'bg-cyan-100 text-cyan-600', ring: 'ring-cyan-500/20', border: 'border-cyan-200', tag: 'bg-cyan-50 text-cyan-700' },
  green: { badge: 'bg-green-100 text-green-800 border-green-200', icon: 'bg-green-100 text-green-600', ring: 'ring-green-500/20', border: 'border-green-200', tag: 'bg-green-50 text-green-700' },
};

const METRICS = [
  { label: 'Construction actions per project', before: '34–129 hrs', after: '7–18 hrs', reduction: '~5x faster' },
  { label: 'Davis-Bacon WD lookup', before: '30–90 min', after: '< 5 min', reduction: '94% reduction' },
  { label: 'Construction IGE preparation', before: '8–40 hrs', after: '1–4 hrs', reduction: '85% reduction' },
  { label: 'Maintenance project status report', before: '4–8 hrs', after: '15 min', reduction: '95% reduction' },
  { label: 'Small business market research + memo', before: '4–16 hrs', after: '30 min', reduction: '87% reduction' },
  { label: 'IFB / solicitation drafting', before: '10–30 hrs', after: '2–5 hrs', reduction: '82% reduction' },
];

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', organization: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, subject: 'ASCP Construction Inquiry' }),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', organization: '', message: '' });
      } else {
        const data = await res.json();
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch (err) {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    }
  };

  const inputCls = 'w-full bg-white/5 border border-white/10 rounded px-4 py-3 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-amber-500/50 focus:bg-white/8 transition-all';
  const labelCls = 'block text-xs font-bold tracking-widest text-amber-400 uppercase mb-2';

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full min-h-[420px] text-center px-8">
        <div className="w-14 h-14 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center">
          <CheckCircle className="w-7 h-7 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-white">Message Sent</h3>
        <p className="text-slate-400 text-sm leading-relaxed max-w-xs">Dr. Woodson will be in touch shortly. You'll receive a reply at the email you provided.</p>
        <button onClick={() => setStatus('idle')} className="text-xs text-slate-500 hover:text-amber-400 transition-colors mt-2 tracking-wide uppercase font-bold">Send Another Message</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5 h-full">
      <div>
        <p className="text-xs font-bold tracking-widest text-amber-400 uppercase mb-2">Send Us a Message</p>
        <h2 className="text-3xl font-bold text-white">Start the <span className="text-amber-400">Conversation.</span></h2>
        <p className="text-slate-400 text-sm mt-2 leading-relaxed">Tell us about your mission or requirement. We'll route your message directly to Dr. Woodson and respond within one business day.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Name *</label>
          <input name="name" value={form.name} onChange={handleChange} required placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@organization.gov" className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Phone</label>
          <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(202) 555-0100" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Organization / Agency</label>
          <input name="organization" value={form.organization} onChange={handleChange} placeholder="Agency or company" className={inputCls} />
        </div>
      </div>
      <div className="flex-1">
        <label className={labelCls}>Message *</label>
        <textarea name="message" value={form.message} onChange={handleChange} required rows={5} placeholder="Tell us about your mission, requirement, or question..." className={`${inputCls} resize-none`} />
      </div>
      {status === 'error' && (
        <p className="text-sm text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" />{errorMsg}</p>
      )}
      <div className="flex items-center gap-4">
        <button type="submit" disabled={status === 'sending'}
          className="px-8 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded transition-colors flex items-center gap-2 text-sm tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'sending' ? <><span className="w-4 h-4 border-2 border-slate-600/40 border-t-slate-900 rounded-full animate-spin" />Sending...</> : <>Send Message <ArrowRight className="w-4 h-4" /></>}
        </button>
        <p className="text-xs text-slate-500">We respond within one business day.</p>
      </div>
    </form>
  );
}

function PainPointCard({ point }) {
  const [expanded, setExpanded] = useState(false);
  const colors = COLOR_MAP[point.color];
  const Icon = point.icon;

  return (
    <div className={`bg-white border ${colors.border} rounded-2xl p-6 hover:shadow-md transition-all ring-1 ${colors.ring}`}>
      <div className="flex items-start gap-4 mb-3">
        <div className={`w-10 h-10 rounded-xl ${colors.icon} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base font-bold text-gray-900">{point.title}</h3>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${colors.badge}`}>
              {point.existing}
            </span>
          </div>
          <p className={`text-xs font-semibold ${colors.tag.split(' ')[1]} italic`}>{point.tag}</p>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-start gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-600 leading-relaxed">{point.problem}</p>
        </div>
        {expanded && (
          <div className="flex items-start gap-2 mt-3">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700 leading-relaxed font-medium">{point.solution}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${colors.tag} border ${colors.border}`}>
          <Clock className="w-3 h-3" />
          {point.timeSaved}
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 font-medium transition-colors"
        >
          {expanded ? 'Less' : 'See solution'}
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );
}

export default function ConstructionLanding() {


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">

      {/* Nav — matches main site */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200/70">
        <div className="w-full px-4 sm:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <a href="/" className="flex items-center gap-3">
                <img alt="AcuSightPro Logo" className="h-14 sm:h-16 w-auto" src="/logo.png" />
              </a>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-full">
                <HardHat className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs font-bold text-amber-800 tracking-wide">CONSTRUCTION</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a href="/" className="text-sm font-medium text-gray-600 hover:text-gray-900 hidden sm:block">← Back to AcuSightPro</a>
              <a
                href="#request-access"
                className="px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/40 transition-all hover:-translate-y-0.5"
              >
                Request Early Access
              </a>
            </div>
          </div>
        </div>
      </header>

      <main>

        {/* Hero */}
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto text-center">

              {/* Headline */}
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="block text-gray-900">The Acquisition Engine</span>
                <span className="block text-gray-900">Federal Construction</span>
                <span className="block bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 bg-clip-text text-transparent">
                  Has Never Had.
                </span>
              </h1>

              <p className="text-xl text-gray-600 mb-4 leading-relaxed max-w-3xl mx-auto">
                AcuSightPro Construction Pro (ASCP) extends the trusted Acumen-10 AI engine into federal construction procurement. Davis-Bacon automation, construction IGEs, facility maintenance pipeline management, JOC task orders, and safety plan review. Built specifically for federal construction contracting officers.
              </p>

              <p className="text-base font-semibold text-purple-700 mb-8 max-w-3xl mx-auto">
                Same SDVOSB-first logic. Same FAR Part 36 compliance framework. Extended for the reality of managing facility maintenance projects, design-build solicitations, and active occupied federal facility construction.
              <span className="block mt-3 text-sm font-bold text-amber-700 tracking-wide">Powered by ACUMEN-10 Engine</span>
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="#request-access"
                  className="px-7 py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/40 transition-all hover:-translate-y-1 flex items-center gap-2"
                >
                  Request Early Access <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#modules"
                  className="px-7 py-3.5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-indigo-50 hover:border-purple-600 transition-all"
                >
                  See All 8 Modules
                </a>
              </div>

          </div>
        </section>

        {/* Context banner */}
        <section className="py-10 px-6 bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="max-w-7xl mx-auto">
            <p className="text-center text-slate-300 text-sm font-medium mb-6 tracking-wide uppercase">The federal construction procurement reality</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { stat: '30,000+', label: 'federal facilities with active construction maintenance programs' },
                { stat: '30–80', label: 'concurrent maintenance projects per facility, tracked in spreadsheets' },
                { stat: '$128B', label: 'federal construction & facilities spend annually' },
                { stat: 'GAO', label: 'repeatedly flags federal construction cost estimates as systemically weak' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl font-bold text-white mb-1">{item.stat}</div>
                  <div className="text-sm text-slate-400 leading-snug">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pain points / modules */}
        <section id="modules" className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4">
                <Hammer className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">8 construction pain points. 8 solutions.</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Built for what construction COs actually deal with</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Every module maps to a documented, real problem in federal construction procurement. No bloat. No features nobody uses.
              </p>
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {PAIN_POINTS.map((point) => (
                <PainPointCard key={point.id} point={point} />
              ))}
            </div>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-purple-200 border border-purple-400 inline-block"></span>
                <strong>Extend</strong>: built on existing AcuSightPro modules
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-200 border border-blue-400 inline-block"></span>
                <strong>New</strong>: construction-specific, built from scratch
              </span>
            </div>
          </div>
        </section>

        {/* Time savings */}
        <section className="py-20 px-6 bg-gradient-to-br from-indigo-50 to-purple-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">The time savings are documented.</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Modeled from published GAO acquisition workforce studies (GAO-25-107075, GAO-25-107437), GSA MRAS data, and RAND DoD workforce analyses. The same sources federal acquisition reviewers rely on.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-200 px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
                <div className="col-span-2">Workflow</div>
                <div className="text-center">Without ASCP</div>
                <div className="text-center text-green-700">With ASCP</div>
              </div>
              {METRICS.map((m, i) => (
                <div key={i} className={`grid grid-cols-4 px-6 py-4 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} border-b border-gray-100`}>
                  <div className="col-span-2 text-sm font-medium text-gray-800">{m.label}</div>
                  <div className="text-center text-sm text-red-600 font-semibold">{m.before}</div>
                  <div className="text-center">
                    <span className="text-sm font-bold text-green-700">{m.after}</span>
                    <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-semibold">{m.reduction}</span>
                  </div>
                </div>
              ))}
              <div className="px-6 py-5 bg-gradient-to-r from-purple-50 to-indigo-50 border-t border-purple-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-bold text-gray-900">Total per construction action</p>
                    <p className="text-sm text-gray-500">At 50 maintenance projects/year per facility</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-red-600 font-semibold">34–129 hrs <span className="text-gray-400 font-normal">traditional</span></p>
                    <p className="text-lg font-bold text-green-700">7–18 hrs with ASCP</p>
                    <p className="text-xs text-purple-700 font-semibold">1,350–5,500 hrs saved / facility / year</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mt-4">
              Model-based estimates using published federal acquisition baseline sources. Live telemetry established under sponsored pilot deployment.
            </p>
          </div>
        </section>

        {/* How it connects to AcuSightPro */}
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">One platform. Two acquisition worlds.</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                ASCP is a construction module within AcuSightPro, not a separate app. Same Acumen-10 engine. Same auth, org, and audit trail. Toggle between services and construction mode for the acquisition in front of you.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-purple-200 rounded-2xl p-6 ring-1 ring-purple-500/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                    <span className="text-xs font-black text-white">ACI</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">AcuSightPro (Services)</h3>
                    <p className="text-xs text-gray-500">IT, professional services, consulting</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {['Market research + RFI generation', 'SOW/PWS/SOO builder + IGCE', 'RFP/RFQ + FAR compliance', 'Proposal evaluation workbench', 'Contract + modifications tracker'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white border border-amber-200 rounded-2xl p-6 ring-1 ring-amber-500/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <HardHat className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">AcuSightPro Construction (ASCP)</h3>
                    <p className="text-xs text-gray-500">Maintenance, minor construction, design-build</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {['Davis-Bacon WD automation', 'Construction IGE builder (WBS + benchmarks)', 'IFB/Design-Build + FAR Part 36 clauses', 'Multi-project maintenance pipeline dashboard', 'JOC task order manager', 'A/E selection (Brooks Act / SF 330)', 'Safety plan review (OSHA + agency standards)'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact section */}
        <section id="request-access" className="py-20 px-6" style={{background: '#0b1929'}}>
          <div className="max-w-5xl mx-auto">

            <div className="grid md:grid-cols-2 rounded-2xl overflow-hidden border border-white/10 min-h-[560px]">

              {/* Left — Dr. Jeff info */}
              <div className="bg-[#0f2240] p-10 flex flex-col gap-6">
                <div>
                  <p className="text-xs font-bold tracking-widest text-slate-400 uppercase mb-4">Speak Directly With</p>
                  <h3 className="text-3xl font-bold text-white mb-2">Dr. Jeff Woodson</h3>
                  <p className="text-amber-400 font-bold text-sm tracking-wide uppercase">President &amp; CEO &nbsp;·&nbsp; Encore Services, LLC</p>
                </div>

                <div className="space-y-5 text-sm flex-1">
                  <div>
                    <p className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-1">Address</p>
                    <p className="text-slate-200">9500 Medical Center Dr. Suite 300</p>
                    <p className="text-slate-200">Largo, MD 20774</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-1">Business</p>
                    <a href="tel:2024608668" className="text-slate-200 hover:text-amber-300 transition-colors">(202) 460-8668</a>
                  </div>
                  <div>
                    <p className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-1">Mobile</p>
                    <a href="tel:3016753001" className="text-slate-200 hover:text-amber-300 transition-colors">(301) 675-3001</a>
                  </div>
                  <div>
                    <p className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-1">Email</p>
                    <a href="mailto:jwoodson@encoresvcsllc.com" className="text-amber-400 hover:text-amber-300 transition-colors">jwoodson@encoresvcsllc.com</a>
                  </div>
                  <div>
                    <p className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-1">Web</p>
                    <a href="https://www.encoresvcsllc.com" target="_blank" rel="noopener noreferrer" className="text-slate-200 hover:text-amber-300 transition-colors">www.encoresvcsllc.com</a>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-5">
                  <p className="text-xs text-slate-500 italic mb-1">A Service-Disabled Veteran-Owned Small Business</p>
                  <p className="text-xs text-slate-600">GSA Schedule 70 &nbsp;·&nbsp; #47QTCA18D00BX</p>
                </div>
              </div>

              {/* Right — contact form */}
              <div className="bg-[#0a1628] p-10 flex flex-col">
                <ContactForm />
              </div>

            </div>
          </div>
        </section>

        {/* Stat pills + badges */}
        <div className="py-10 px-6 bg-white border-t border-gray-100">
          <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-4 mb-5">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-full">
              <div className="w-5 h-5 rounded bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                <span className="text-[7px] font-black text-white">ACI</span>
              </div>
              <span className="text-sm font-semibold text-purple-800 tracking-wide">ACUMEN-10 Engine</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
              <HardHat className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-bold text-amber-800">ASCP: AcuSightPro Construction Pro</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-gray-50 to-indigo-50/50 border border-gray-200 rounded-full">
              <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
              <span className="text-xs font-semibold text-gray-700">U.S. Patent Pending #63/987,765</span>
            </div>
          </div>
          <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-4">
            {[
              { label: '~5x faster', sub: 'per construction action' },
              { label: '1,350–5,500 hrs', sub: 'saved per facility / year' },
              { label: '8 modules', sub: 'purpose-built for construction COs' },
              { label: 'SDVOSB prime', sub: 'built by Encore Services LLC' },
            ].map((s, i) => (
              <div key={i} className="px-5 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="text-base font-bold text-gray-900">{s.label}</div>
                <div className="text-xs text-gray-500">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer strip */}
        <div className="py-6 px-6 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img alt="AcuSightPro" className="h-8 w-auto" src="/logo.png" />
              <span className="text-sm text-gray-500">AcuSightPro Construction Pro (ASCP) · Brought to you by Encore Services LLC</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span>U.S. Patent Pending #63/987,765</span>
              <span>·</span>
              <span>SDVOSB Prime Contractor</span>
              <span>·</span>
              <a href="https://encoresvcsllc.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">Encore Services LLC</a>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}
